import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  ListObservable,
  Milestone,
  ObjectObservable,
} from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { InputSize } from '@freelancer/ui/input';
import { Margin } from '@freelancer/ui/margin';
import { required } from '@freelancer/ui/validators';
import * as Rx from 'rxjs';
import { MilestoneActionReasons } from '../milestone-area/milestone-area.types';
@Component({
  template: `
    <fl-bit
      [flMarginBottom]="Margin.MID"
    >
      <fl-heading
        [size]="HeadingSize.XSMALL"
        [headingType]="HeadingType.H4"
        i18n="Cancel milestone modal header"
      >
        Milestone Cancellation Request
      </fl-heading>
    </fl-bit>

    <fl-bit class="MainBody"
      [flMarginBottom]="Margin.SMALL"
    >
      <fl-text
        [flMarginBottom]="Margin.SMALL"
        i18n="Cancel milestone modal body"
      >
        In order to cancel a milestone, you need to send a cancellation request to your freelancer. Once they accept the request, the milestone will be cancelled and the funds will be returned to you. If the request is denied, you can initiate a dispute
          <fl-link [link]="'/dispute/disputeslist.php'">here</fl-link>.
      </fl-text>
    </fl-bit>

    <fl-bit class="MilestoneInfo"
      [flMarginBottom]="Margin.SMALL"
    >
      <fl-bit class="MilestoneInfoText">
        <fl-text
          [flMarginRight]="Margin.MID"
          i18n="Cancel milestone modal project name text"
        >
          Project Name
        </fl-text>
        <fl-text>
          {{ projectTitle }}
        </fl-text>
      </fl-bit>
      <fl-bit class="MilestoneInfoText">
        <fl-text
          [flMarginRight]="Margin.MID"
          i18n="Cancel milestone modal freelancer name text"
        >
          Freelancer Name
        </fl-text>
        <fl-text>
          {{ bidderName }}
        </fl-text>
      </fl-bit>
      <fl-bit class="MilestoneInfoText">
        <fl-text
          [flMarginRight]="Margin.MID"
          i18n="Cancel milestone modal description text"
        >
          Milestone Description
        </fl-text>
        <fl-text>
          {{ milestone.description }}
        </fl-text>
      </fl-bit>
      <fl-bit class="MilestoneInfoText">
        <fl-text
          [flMarginRight]="Margin.MID"
          i18n="Cancel milestone modal amount text"
        >
          Milestone Amount
        </fl-text>
        <fl-text>
          {{ milestone.amount }}
        </fl-text>
      </fl-bit>
      <fl-bit class="MilestoneInfoText">
        <fl-text
          [flMarginRight]="Margin.MID"
          i18n="Cancel milestone modal date text"
        >
          Date Created
        </fl-text>
        <fl-text>
          {{ (milestone.timeCreated * 1000) | date }}
        </fl-text>
      </fl-bit>
    </fl-bit>

    <fl-bit
      [flMarginBottom]="Margin.SMALL"
    >
      <fl-heading
        [flMarginBottom]="Margin.XSMALL"
        [size]="HeadingSize.XXSMALL"
        [headingType]="HeadingType.H5"
        i18n="Cancel milestone modal reason heading"
      >
        Why do you want to cancel this milestone?
      </fl-heading>
      <fl-select
        [control]="selector"
        [options]="reasons"
        [placeholder]="'Please select a reason'"
      >
      </fl-select>
    </fl-bit>

    <fl-bit *ngIf="(selectedOption$ | async) === 'other'"
      [flMarginBottom]="Margin.SMALL"
    >
      <fl-heading
        [flMarginBottom]="Margin.XSMALL"
        [size]="HeadingSize.XXSMALL"
        [headingType]="HeadingType.H5"
        i18n="Cancel milestone modal other reason heading"
      >
        Why do you want to cancel it? (Shown to your Freelancer)
      </fl-heading>
      <fl-input
        [control]="otherTextControl"
        [placeholder]="'Please let your Freelancer know why you would like to cancel this milestone'"
        [size]="InputSize.LARGE"
      ></fl-input>
    </fl-bit>

    <fl-bit class="ButtonZone">
      <fl-button
        flTrackingLabel="cancelMilestoneRequestCancelButton"
        [color]="ButtonColor.SECONDARY"
        [flMarginRight]="Margin.XXXSMALL"
        (click)="cancel()"
        i18n="Cancel milestone modal request button"
      >
        Request Cancellation
      </fl-button>
    </fl-bit>
  `,
  styleUrls: ['./milestone-cancel-modal.component.scss'],
})
export class MilestoneCancelModalComponent implements OnInit {
  ButtonColor = ButtonColor;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;
  InputSize = InputSize;
  Margin = Margin;

  @Input() bidderName: string;
  @Input() milestone: Milestone;
  @Input() projectTitle: string;
  @Input() milestoneList$: ObjectObservable<'milestones'>;

  reasons: ReadonlyArray<any> = [
    {
      displayText: "The Freelancer didn't meet the deadline",
      value: MilestoneActionReasons.DEADLINE,
    },
    {
      displayText: 'The milestone was created accidentally',
      value: MilestoneActionReasons.ACCIDENT,
    },
    {
      displayText: "The Freelancer couldn't complete the task",
      value: MilestoneActionReasons.INCOMPLETE,
    },
    {
      displayText: 'The milestone is no longer needed',
      value: MilestoneActionReasons.NO_LONGER_NEEDED,
    },
    {
      displayText: 'Other',
      value: MilestoneActionReasons.OTHER,
    },
  ];
  selector = new FormControl(this.reasons[0].value, [
    required('Please select a reason'),
  ]);
  otherTextControl = new FormControl('', [required('Please provide feedback')]);
  selectedOption$: Rx.Observable<string>; // tracks the selected dropdown option
  bidder: string;

  constructor(
    private modalRef: ModalRef<MilestoneCancelModalComponent>,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.selectedOption$ = this.selector.valueChanges;
  }

  cancel() {
    this.milestoneList$
      .update({
        ...this.milestone,
        action: 'request_cancel',
        actionReason: this.selector.value,
        actionReasonText:
          this.selector.value === 'other' ? this.otherTextControl.value : '',
      })
      .then(response => {
        console.log('cancel backend response:', response);
        this.changeDetectorRef.markForCheck();
      });
    this.modalRef.close(false);
  }
}
